import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const totalPages = Math.ceil(totalRecords / recordsPerPage) || 1;
  const searchDebounceRef = useRef(null);
  const dropdownRef = useRef(null);

  // Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState(null);

  // Form Fields
  const [form, setForm] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
    type: 'package',
    typeId: '',
    rating: 5,
    message: '',
    whoDidYouGo: '',
    whenDidYouGo: '',
    status: 1,
    photos: [],
    reviewerImage: ''
  });

  // Photo uploads (review gallery)
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);

  // Reviewer avatar upload
  const [reviewerFile, setReviewerFile] = useState(null);
  const [reviewerPreview, setReviewerPreview] = useState('');

  // Autocomplete Target State
  const [targetSearch, setTargetSearch] = useState('');
  const [targetOptions, setTargetOptions] = useState([]);
  const [targetLoading, setTargetLoading] = useState(false);
  const [selectedTargetName, setSelectedTargetName] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const targetDebounceRef = useRef(null);

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: searchKeyword,
        type: typeFilter
      };
      
      const response = await apiService.get('admin/reviews', params);
      if (response && response.status) {
        setReviews(response.data);
        setTotalRecords(response.recordsFiltered || 0);
      }
    } catch (error) {
      toast.error('Failed to load reviews');
      console.error('Reviews fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, searchKeyword, typeFilter]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Click outside suggestions dropdown handler to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search input debouncer
  const handleSearch = (value) => {
    setSearchInput(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchKeyword(value);
      setCurrentPage(1);
    }, 500);
  };

  // Dynamic dropdown list search for target
  const fetchTargets = useCallback(async (type, search) => {
    if (!type) return;
    try {
      setTargetLoading(true);
      const response = await apiService.get('admin/reviews/targets', { type, search });
      if (response && response.status) {
        setTargetOptions(response.data);
      }
    } catch (error) {
      console.error('Error fetching targets:', error);
    } finally {
      setTargetLoading(false);
    }
  }, []);

  // Handle target input search change
  const handleTargetSearchChange = (val) => {
    setTargetSearch(val);
    setDropdownOpen(true);
    clearTimeout(targetDebounceRef.current);
    targetDebounceRef.current = setTimeout(() => {
      fetchTargets(form.type, val);
    }, 300);
  };

  // Trigger initial target list when opening or changing target type
  useEffect(() => {
    if (modalOpen) {
      setTargetOptions([]);
      if (!form.id) {
        setForm(prev => ({ ...prev, typeId: '' }));
        setSelectedTargetName('');
      }
      setTargetSearch('');
      fetchTargets(form.type, '');
    }
  }, [form.type, modalOpen, fetchTargets, form.id]);

  const handleAddNew = () => {
    setForm({
      id: '',
      name: '',
      email: '',
      phone: '',
      type: 'package',
      typeId: '',
      rating: 5,
      message: '',
      whoDidYouGo: '',
      whenDidYouGo: '',
      status: 1,
      photos: [],
      reviewerImage: ''
    });
    setSelectedTargetName('');
    setTargetSearch('');
    setSelectedFiles([]);
    setFilePreviews([]);
    setReviewerFile(null);
    setReviewerPreview('');
    setModalOpen(true);
  };

  const handleEdit = (review) => {
    setForm({
      id: review._id,
      name: review.userId?.first_name || '',
      email: review.userId?.email || '',
      phone: review.userId?.phone || '',
      type: review.type,
      typeId: review.typeId?._id || '',
      rating: review.rating,
      message: review.message,
      whoDidYouGo: review.whoDidYouGo || '',
      whenDidYouGo: review.whenDidYouGo ? new Date(review.whenDidYouGo).toISOString().split('T')[0] : '',
      status: review.status,
      photos: review.photos || [],
      reviewerImage: review.userId?.image || ''
    });
    setSelectedTargetName(review.targetName || '');
    setTargetSearch(review.targetName || '');
    setSelectedFiles([]);
    setFilePreviews([]);
    setReviewerFile(null);
    setReviewerPreview('');
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.typeId) {
      toast.error('Please select a target entity from the list');
      return;
    }
    setModalLoading(true);
    try {
      const formData = new FormData();
      Object.keys(form).forEach(key => {
        if (key === 'photos') {
          formData.append('photos', JSON.stringify(form.photos));
        } else {
          formData.append(key, form[key]);
        }
      });
      selectedFiles.forEach(file => {
        formData.append('images', file);
      });
      if (reviewerFile) {
        formData.append('image', reviewerFile);
      }

      const response = await apiService.postFormData('admin/reviews', formData);
      if (response && response.status) {
        toast.success(response.message || 'Review saved successfully');
        setModalOpen(false);
        fetchReviews();
      } else {
        toast.error(response.message || 'Failed to save review');
      }
    } catch (error) {
      toast.error('Server error while saving review');
      console.error(error);
    } finally {
      setModalLoading(false);
    }
  };

  const confirmDelete = (id) => {
    setSelectedReviewId(id);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      const response = await apiService.delete(`admin/reviews/${selectedReviewId}`);
      if (response && response.status) {
        toast.success(response.message || 'Review deleted successfully');
        setDeleteModalOpen(false);
        fetchReviews();
      } else {
        toast.error(response.message || 'Failed to delete review');
      }
    } catch (error) {
      toast.error('Server error while deleting review');
    }
  };

  const handleStatusToggle = async (id) => {
    try {
      const response = await apiService.put(`admin/reviews/${id}/status`);
      if (response && response.status) {
        toast.success(response.message || 'Status updated successfully');
        setReviews(prev => prev.map(r => r._id === id ? { ...r, status: r.status === 1 ? 0 : 1 } : r));
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Something went wrong!!');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i
          key={i}
          className={`bi ${i <= rating ? 'bi-star-fill text-amber-400' : 'bi-star text-slate-300'} text-xs mr-0.5`}
        ></i>
      );
    }
    return stars;
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50 p-6">
      {/* Breadcrumb */}
      <div className="mb-4">
        <nav className="flex items-center space-x-2 text-sm text-gray-600">
          <a href="/dashboard" className="hover:text-gray-900">Home</a>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900">Review Management</span>
        </nav>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {/* Header Section with Filters */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 px-6 gap-4 border-b border-slate-100 bg-white">
          <div>
            <h3 className="text-base font-bold text-slate-900">Reviews List</h3>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto md:justify-end">
            {/* Search Box */}
            <div className="relative w-full sm:w-60">
              <i className="bi bi-search absolute left-3 top-2.5 text-slate-400 text-sm"></i>
              <input
                type="text"
                placeholder="Search reviewer or message..."
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#009ef7]/20"
              />
            </div>
            
            {/* Filter Dropdown */}
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
              className="w-full sm:w-auto px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#009ef7]/20"
            >
              <option value="">All Target Types</option>
              <option value="package">Package</option>
              <option value="vendor">Vendor</option>
              <option value="hotel">Hotel</option>
              <option value="vehicle">Vehicle</option>
              <option value="user">User</option>
              <option value="influencer">Influencer</option>
              <option value="guide">Guide</option>
              <option value="proposal">Proposal</option>
              <option value="query">Query</option>
              <option value="country">Country</option>
              <option value="state">State</option>
              <option value="location">Location</option>
              <option value="blog">Blog</option>
            </select>

            {/* Add Button */}
            <button
              onClick={handleAddNew}
              className="flex items-center justify-center gap-1.5 px-4 py-2 bg-[#009ef7] text-white rounded-xl hover:bg-[#009ef7]/90 text-xs font-semibold shadow-sm transition-all w-full sm:w-auto whitespace-nowrap"
            >
              <i className="bi bi-plus-lg"></i>
              Add Review
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-500 uppercase text-[10px] font-bold tracking-wider border-b border-slate-100">
                <th className="px-2 py-3.5 text-center w-10">#</th>
                <th className="px-3 py-3.5 w-44">Reviewer</th>
                <th className="px-4 py-3.5 min-w-[240px]">Entity Target</th>
                <th className="px-4 py-3.5">Rating & Message</th>
                <th className="px-4 py-3.5">Trip Details</th>
                <th className="px-4 py-3.5 text-center">Status / Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-slate-500">
                    <div className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-[#009ef7] border-t-transparent rounded-full animate-spin"></span>
                      Loading reviews...
                    </div>
                  </td>
                </tr>
              ) : reviews.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-slate-500">
                    <i className="bi bi-chat-square-text text-3xl block mb-2 text-slate-300"></i>
                    No reviews found.
                  </td>
                </tr>
              ) : (
                reviews.map((r, idx) => (
                  <tr key={r._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-2 py-3.5 text-center text-slate-400 font-medium w-10">
                      {(currentPage - 1) * recordsPerPage + idx + 1}
                    </td>
                    <td className="px-3 py-3.5 w-44">
                      <div className="flex items-center gap-2">
                        {/* Reviewer Profile Image */}
                        <div className="relative w-8 h-8 rounded-full overflow-hidden border border-slate-200 bg-slate-100 flex-shrink-0 flex items-center justify-center text-slate-400 font-bold text-xs uppercase">
                          {r.userId?.image ? (
                            <img
                              src={`${process.env.REACT_APP_MEDIA_URL}${r.userId.image}`}
                              alt={r.userId?.first_name || 'User'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            (r.userId?.first_name ? r.userId.first_name.charAt(0) : 'U')
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 truncate">
                            {r.userId ? `${r.userId.first_name || ''} ${r.userId.last_name || ''}`.trim() : 'Anonymous'}
                          </div>
                          <div className="text-[10px] text-slate-400 truncate">{r.userId?.email || 'No Email'}</div>
                          <div className="text-[10px] text-slate-400 truncate">{r.userId?.phone || 'No Phone'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 min-w-[240px]">
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        <span className="font-medium text-slate-800">{r.targetName}</span>
                        {r.type && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${
                            r.type === 'package' ? 'bg-sky-50 text-sky-700' :
                            r.type === 'vendor' ? 'bg-indigo-50 text-indigo-700' :
                            r.type === 'hotel' ? 'bg-amber-50 text-amber-700' :
                            r.type === 'vehicle' ? 'bg-emerald-50 text-emerald-700' :
                            r.type === 'user' ? 'bg-slate-100 text-slate-700' :
                            r.type === 'influencer' ? 'bg-pink-50 text-pink-700' :
                            r.type === 'guide' ? 'bg-violet-50 text-violet-700' :
                            r.type === 'proposal' ? 'bg-rose-50 text-rose-700' :
                            r.type === 'query' ? 'bg-orange-50 text-orange-700' :
                            r.type === 'country' ? 'bg-blue-50 text-blue-700' :
                            r.type === 'state' ? 'bg-cyan-50 text-cyan-700' :
                            r.type === 'location' ? 'bg-teal-50 text-teal-700' :
                            'bg-purple-50 text-purple-700'
                          }`}>
                            {r.type}
                          </span>
                        )}
                      </div>
                      {r.vendorId && (
                        <div className="text-[10px] text-slate-400">
                          Vendor: {r.vendorId.business_name || `${r.vendorId.first_name} ${r.vendorId.last_name}`}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center mb-1">
                        {renderStars(r.rating)}
                        <span className="text-[10px] font-bold text-slate-500 ml-1.5">{r.rating}/5</span>
                      </div>
                      <p className="text-slate-600 line-clamp-2" title={r.message}>{r.message}</p>
                      
                      {/* Image List Previews */}
                      {r.photos && r.photos.length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {r.photos.map((photo, pIdx) => (
                            <a
                              key={pIdx}
                              href={`${process.env.REACT_APP_MEDIA_URL}${photo}`}
                              target="_blank"
                              rel="noreferrer"
                              className="relative w-8 h-8 rounded-lg overflow-hidden border border-slate-200 hover:scale-105 transition-all group"
                            >
                              <img
                                src={`${process.env.REACT_APP_MEDIA_URL}${photo}`}
                                alt={`Review ${pIdx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </a>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3.5 whitespace-nowrap text-slate-500">
                      <div>With: {r.whoDidYouGo || '--'}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">When: {formatDate(r.whenDidYouGo)}</div>
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <label className="relative inline-flex items-center cursor-pointer" title="Toggle Status">
                          <input
                            type="checkbox"
                            checked={r.status === 1}
                            onChange={() => handleStatusToggle(r._id)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#009ef7]"></div>
                        </label>
                        <button
                          onClick={() => handleEdit(r)}
                          className="w-7 h-7 inline-flex items-center justify-center text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                          title="Edit Review"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          onClick={() => confirmDelete(r._id)}
                          className="w-7 h-7 inline-flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                          title="Delete Review"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        {totalRecords > 0 && reviews.length > 0 && (
          <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-slate-600 text-xs">
              <span>Show</span>
              <select
                value={recordsPerPage}
                onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="px-2 py-1 border border-slate-200 rounded-lg bg-white focus:outline-none"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
              <span className="text-slate-400 hidden sm:inline">|</span>
              <span className="text-slate-400 hidden sm:inline">
                Displaying {(currentPage - 1) * recordsPerPage + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} records
              </span>
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs disabled:opacity-50 hover:bg-white text-slate-700 bg-slate-50 font-semibold"
                title="First Page"
              >
                &laquo;
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs disabled:opacity-50 hover:bg-white text-slate-700 bg-slate-50 font-semibold"
                title="Previous Page"
              >
                &lsaquo;
              </button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 border rounded-lg text-xs font-semibold ${currentPage === page ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-white'}`}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-1 text-slate-400">...</span>;
                }
                return null;
              })}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs disabled:opacity-50 hover:bg-white text-slate-700 bg-slate-50 font-semibold"
                title="Next Page"
              >
                &rsaquo;
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs disabled:opacity-50 hover:bg-white text-slate-700 bg-slate-50 font-semibold"
                title="Last Page"
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Review Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-100 bg-slate-50">
              <div>
                <h3 className="text-sm font-bold text-slate-900 leading-tight">{form.id ? "Edit Review" : "Add Review Manually"}</h3>
                <p className="text-[9px] text-slate-400 leading-none mt-0.5">Fill in details below to link reviews directly</p>
              </div>
              <button type="button" onClick={() => setModalOpen(false)} className="h-6 w-6 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-100 flex items-center justify-center">
                <i className="bi bi-x text-sm"></i>
              </button>
            </div>

            {/* Modal Form Content */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* User lookup info */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-3">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Reviewer Details (Will auto-link if user exists)</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Reviewer Profile Image Preview and Input */}
                  <div className="flex flex-col items-center justify-center gap-2 flex-shrink-0">
                    <div className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-slate-200 bg-slate-100 flex items-center justify-center text-slate-400 font-extrabold text-xl uppercase">
                      {reviewerPreview ? (
                        <img src={reviewerPreview} alt="Reviewer profile preview" className="w-full h-full object-cover" />
                      ) : form.reviewerImage ? (
                        <img src={`${process.env.REACT_APP_MEDIA_URL}${form.reviewerImage}`} alt="Reviewer profile" className="w-full h-full object-cover" />
                      ) : (
                        (form.name ? form.name.charAt(0) : '?')
                      )}
                    </div>
                    <label className="cursor-pointer px-2 py-1 bg-white border border-slate-200 rounded-lg text-[9px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors shadow-sm">
                      Upload Profile
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setReviewerFile(file);
                            setReviewerPreview(URL.createObjectURL(file));
                          }
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <div className="flex-1 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Full Name</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. John Doe"
                          value={form.name}
                          onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#009ef7]/20"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-600 mb-1">Phone Number</label>
                        <input
                          type="text"
                          placeholder="e.g. 9876543210"
                          value={form.phone}
                          onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#009ef7]/20"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-600 mb-1">Email Address</label>
                      <input
                        type="email"
                        placeholder="e.g. john@example.com"
                        value={form.email}
                        onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#009ef7]/20"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Review targets dropdown & query search input */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1">Review Target Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm(p => ({ ...p, type: e.target.value, typeId: '' }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#009ef7]/20"
                  >
                    <option value="package">Package</option>
                    <option value="vendor">Vendor</option>
                    <option value="hotel">Hotel</option>
                    <option value="vehicle">Vehicle</option>
                    <option value="user">User</option>
                    <option value="influencer">Influencer</option>
                    <option value="guide">Guide</option>
                    <option value="proposal">Proposal</option>
                    <option value="query">Query</option>
                    <option value="country">Country</option>
                    <option value="state">State</option>
                    <option value="location">Location</option>
                    <option value="blog">Blog</option>
                  </select>
                </div>

                <div ref={dropdownRef} className="relative">
                  <label className="block text-[10px] font-bold text-slate-600 mb-1">
                    Select Target Entity <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={`Search ${form.type}...`}
                      value={targetSearch}
                      onChange={(e) => handleTargetSearchChange(e.target.value)}
                      onFocus={() => setDropdownOpen(true)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#009ef7]/20"
                    />
                    {targetLoading && (
                      <span className="absolute right-3 top-2.5 w-3.5 h-3.5 border-2 border-[#009ef7] border-t-transparent rounded-full animate-spin"></span>
                    )}
                  </div>
                  
                  {/* Custom Search Suggestion list */}
                  {dropdownOpen && targetOptions.length > 0 && (
                    <div className="absolute left-0 right-0 z-50 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                      {targetOptions.map((opt) => (
                        <div
                          key={opt.id}
                          onClick={() => {
                            setForm(p => ({ ...p, typeId: opt.id }));
                            setSelectedTargetName(opt.name);
                            setTargetSearch(opt.name);
                            setDropdownOpen(false);
                          }}
                          className="px-3 py-2 hover:bg-slate-50 cursor-pointer text-xs text-slate-700 transition-colors"
                        >
                          {opt.name}
                        </div>
                      ))}
                    </div>
                  )}
                  {dropdownOpen && targetSearch && targetOptions.length === 0 && !targetLoading && (
                    <div className="absolute left-0 right-0 z-50 mt-1 p-3 bg-white border border-slate-200 rounded-lg shadow-lg text-xs text-slate-400 text-center">
                      No matching targets found
                    </div>
                  )}
                  {form.typeId && (
                    <div className="text-[10px] text-emerald-600 font-semibold mt-1">
                      Selected: {selectedTargetName}
                    </div>
                  )}
                </div>
              </div>

              {/* Message, Rating */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1">Rating Stars</label>
                  <select
                    value={form.rating}
                    onChange={(e) => setForm(p => ({ ...p, rating: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#009ef7]/20"
                  >
                    <option value={5}>5 Stars</option>
                    <option value={4}>4 Stars</option>
                    <option value={3}>3 Stars</option>
                    <option value={2}>2 Stars</option>
                    <option value={1}>1 Star</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm(p => ({ ...p, status: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#009ef7]/20"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-600 mb-1 font-semibold">Review Message <span className="text-rose-500">*</span></label>
                <textarea
                  required
                  rows="5"
                  placeholder="Share feedback comments..."
                  value={form.message}
                  onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#009ef7]/20 resize-y min-h-[100px]"
                ></textarea>
              </div>

              {/* Image Upload field */}
              <div className="border-t border-slate-100 pt-3">
                <label className="block text-[10px] font-bold text-slate-600 mb-1 font-semibold">Review Photos</label>
                
                {/* Existing Photos List */}
                {form.photos && form.photos.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[9px] text-slate-400 mb-1">Existing photos (click trash icon to remove):</p>
                    <div className="flex gap-2 flex-wrap">
                      {form.photos.map((photo, pIdx) => (
                        <div key={pIdx} className="relative w-12 h-12 rounded-lg border border-slate-200 overflow-hidden group">
                          <img
                            src={`${process.env.REACT_APP_MEDIA_URL}${photo}`}
                            alt="Existing review file"
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setForm(prev => ({
                                ...prev,
                                photos: prev.photos.filter((_, idx) => idx !== pIdx)
                              }));
                            }}
                            className="absolute inset-0 bg-black/45 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove photo"
                          >
                            <i className="bi bi-trash text-xs"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Files Upload */}
                <div className="space-y-2">
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-slate-200 border-dashed rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-4 pb-3">
                        <i className="bi bi-cloud-arrow-up text-xl text-slate-400 mb-1"></i>
                        <p className="text-[10px] text-slate-500 font-medium">Click to select new review images</p>
                        <p className="text-[8px] text-slate-400">PNG, JPG, JPEG (Max 10 files)</p>
                      </div>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          setSelectedFiles(prev => [...prev, ...files]);
                          const previews = files.map(file => URL.createObjectURL(file));
                          setFilePreviews(prev => [...prev, ...previews]);
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* Selected New Previews */}
                  {filePreviews.length > 0 && (
                    <div>
                      <p className="text-[9px] text-slate-400 mb-1">New photos to upload:</p>
                      <div className="flex gap-2 flex-wrap">
                        {filePreviews.map((preview, pIdx) => (
                          <div key={pIdx} className="relative w-12 h-12 rounded-lg border border-slate-200 overflow-hidden group">
                            <img
                              src={preview}
                              alt="New upload preview"
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedFiles(prev => prev.filter((_, idx) => idx !== pIdx));
                                setFilePreviews(prev => prev.filter((_, idx) => idx !== pIdx));
                              }}
                              className="absolute inset-0 bg-black/45 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Cancel upload"
                            >
                              <i className="bi bi-x-circle text-xs"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Extra Optional info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-slate-100 pt-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1">Who did they travel with? (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. Family, Solo, Friends"
                    value={form.whoDidYouGo}
                    onChange={(e) => setForm(p => ({ ...p, whoDidYouGo: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1">When did they travel? (Optional)</label>
                  <input
                    type="date"
                    value={form.whenDidYouGo}
                    onChange={(e) => setForm(p => ({ ...p, whenDidYouGo: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="px-4 py-2 bg-[#009ef7] text-white hover:bg-[#009ef7]/90 disabled:opacity-50 rounded-xl text-xs font-semibold flex items-center gap-1.5"
                >
                  {modalLoading ? (
                    <>
                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle"></i>
                      Save Review
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl border border-slate-200 p-6 space-y-4">
            <div className="flex items-center gap-3 text-rose-600">
              <div className="w-10 h-10 bg-rose-50 border border-rose-100 rounded-full flex items-center justify-center">
                <i className="bi bi-trash text-lg"></i>
              </div>
              <h3 className="text-base font-bold text-slate-900">Delete Review</h3>
            </div>
            <p className="text-xs text-slate-500">
              Are you sure you want to delete this review? This action is permanent and cannot be undone.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeleteModalOpen(false)}
                className="px-4 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-rose-600 text-white hover:bg-rose-700 rounded-xl text-xs font-semibold"
              >
                Delete Permanent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
